import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Lock, Mail, Shield, ArrowLeft, Check, AlertTriangle } from "lucide-react"
import { useAuth } from "../contexts/AuthContext"
import { authAPI } from "../lib/supabase"

function SettingsPage() {
	const { user, profile } = useAuth()
	const navigate = useNavigate()

	// Password change state
	const [newPassword, setNewPassword] = useState("")
	const [confirmPassword, setConfirmPassword] = useState("")
	const [passwordLoading, setPasswordLoading] = useState(false)
	const [passwordMessage, setPasswordMessage] = useState(null) // { type: 'success' | 'error', text }

	// Password reset via email
	const [resetLoading, setResetLoading] = useState(false)
	const [resetMessage, setResetMessage] = useState(null)

	if (!user) {
		navigate("/login")
		return null
	}

	const isOAuthUser = user.app_metadata?.provider === "google" || user.identities?.some(i => i.provider === "google")

	const handleChangePassword = async () => {
		if (!newPassword || newPassword.length < 8) {
			setPasswordMessage({ type: "error", text: "Password must be at least 8 characters." })
			return
		}
		if (newPassword !== confirmPassword) {
			setPasswordMessage({ type: "error", text: "Passwords do not match." })
			return
		}

		setPasswordLoading(true)
		setPasswordMessage(null)
		try {
			const { error } = await authAPI.updatePassword(newPassword)
			if (error) {
				setPasswordMessage({ type: "error", text: error.message || "Failed to update password." })
			} else {
				setPasswordMessage({ type: "success", text: "Password updated successfully!" })
				setNewPassword("")
				setConfirmPassword("")
			}
		} catch (err) {
			setPasswordMessage({ type: "error", text: "Something went wrong. Please try again." })
		} finally {
			setPasswordLoading(false)
		}
	}

	const handleSendResetEmail = async () => {
		setResetLoading(true)
		setResetMessage(null)
		try {
			const { error } = await authAPI.resetPassword(user.email)
			if (error) {
				setResetMessage({ type: "error", text: error.message || "Failed to send reset email." })
			} else {
				setResetMessage({ type: "success", text: `Reset link sent to ${user.email}` })
			}
		} catch (err) {
			setResetMessage({ type: "error", text: "Something went wrong." })
		} finally {
			setResetLoading(false)
		}
	}

	return (
		<div className="min-h-screen bg-dark">
			<div className="max-w-2xl mx-auto px-4 py-8">
				{/* Header */}
				<div className="flex items-center gap-3 mb-8">
					<button
						onClick={() => navigate(-1)}
						className="p-2 text-cream/50 hover:text-cream hover:bg-white/5 rounded-lg transition-colors"
					>
						<ArrowLeft size={20} />
					</button>
					<div>
						<h1 className="text-2xl font-bold text-cream">Settings</h1>
						<p className="text-cream/50 text-sm">Manage your account</p>
					</div>
				</div>

				{/* Account Info */}
				<section className="bg-[#12121a] border border-purple/10 rounded-xl p-5 mb-6">
					<h2 className="text-lg font-semibold text-cream mb-4 flex items-center gap-2">
						<Shield size={18} className="text-purple" />
						Account
					</h2>

					<div className="space-y-3">
						<div className="flex items-center justify-between py-2">
							<div>
								<div className="text-sm text-cream/70">Email</div>
								<div className="text-cream">{user.email}</div>
							</div>
							<Mail size={16} className="text-cream/30" />
						</div>

						<div className="border-t border-purple/10" />

						<div className="flex items-center justify-between py-2">
							<div>
								<div className="text-sm text-cream/70">Sign-in method</div>
								<div className="text-cream">{isOAuthUser ? "Google" : "Email & Password"}</div>
							</div>
						</div>

						{profile?.username && (
							<>
								<div className="border-t border-purple/10" />
								<div className="flex items-center justify-between py-2">
									<div>
										<div className="text-sm text-cream/70">Username</div>
										<div className="text-cream">@{profile.username}</div>
									</div>
								</div>
							</>
						)}
					</div>
				</section>

				{/* Password Section */}
				{!isOAuthUser ? (
					<section className="bg-[#12121a] border border-purple/10 rounded-xl p-5 mb-6">
						<h2 className="text-lg font-semibold text-cream mb-4 flex items-center gap-2">
							<Lock size={18} className="text-purple" />
							Change Password
						</h2>

						<div className="space-y-4">
							<div>
								<label className="block text-sm text-cream/70 mb-1.5">New Password</label>
								<input
									type="password"
									value={newPassword}
									onChange={(e) => setNewPassword(e.target.value)}
									placeholder="Minimum 8 characters"
									className="w-full px-3 py-2.5 bg-dark border border-purple/20 rounded-lg text-cream text-sm
										placeholder-cream/30 focus:outline-none focus:ring-2 focus:ring-purple/50 focus:border-purple/50"
								/>
							</div>

							<div>
								<label className="block text-sm text-cream/70 mb-1.5">Confirm New Password</label>
								<input
									type="password"
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									placeholder="Re-enter password"
									className="w-full px-3 py-2.5 bg-dark border border-purple/20 rounded-lg text-cream text-sm
										placeholder-cream/30 focus:outline-none focus:ring-2 focus:ring-purple/50 focus:border-purple/50"
								/>
							</div>

							{passwordMessage && (
								<div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
									passwordMessage.type === "success"
										? "bg-green-500/10 text-green-400 border border-green-500/20"
										: "bg-red-500/10 text-red-400 border border-red-500/20"
								}`}>
									{passwordMessage.type === "success" ? <Check size={14} /> : <AlertTriangle size={14} />}
									{passwordMessage.text}
								</div>
							)}

							<button
								onClick={handleChangePassword}
								disabled={passwordLoading || !newPassword || !confirmPassword}
								className="px-5 py-2.5 bg-purple hover:bg-purple/90 disabled:bg-gray-700 disabled:text-cream/30
									text-white rounded-lg text-sm font-medium transition-colors"
							>
								{passwordLoading ? "Updating…" : "Update Password"}
							</button>
						</div>
					</section>
				) : (
					<section className="bg-[#12121a] border border-purple/10 rounded-xl p-5 mb-6">
						<h2 className="text-lg font-semibold text-cream mb-2 flex items-center gap-2">
							<Lock size={18} className="text-purple" />
							Password
						</h2>
						<p className="text-cream/50 text-sm">
							Your account uses Google sign-in. Password management is handled through your Google account.
						</p>
					</section>
				)}

				{/* Password Reset via Email */}
				{!isOAuthUser && (
					<section className="bg-[#12121a] border border-purple/10 rounded-xl p-5 mb-6">
						<h2 className="text-sm font-semibold text-cream mb-2">Forgot your password?</h2>
						<p className="text-cream/50 text-xs mb-3">
							We'll send a reset link to <span className="text-cream">{user.email}</span>
						</p>

						{resetMessage && (
							<div className={`flex items-center gap-2 p-3 rounded-lg text-sm mb-3 ${
								resetMessage.type === "success"
									? "bg-green-500/10 text-green-400 border border-green-500/20"
									: "bg-red-500/10 text-red-400 border border-red-500/20"
							}`}>
								{resetMessage.type === "success" ? <Check size={14} /> : <AlertTriangle size={14} />}
								{resetMessage.text}
							</div>
						)}

						<button
							onClick={handleSendResetEmail}
							disabled={resetLoading}
							className="px-4 py-2 bg-dark border border-purple/20 hover:border-purple/50 text-cream/70 hover:text-cream
								rounded-lg text-sm transition-colors disabled:opacity-50"
						>
							{resetLoading ? "Sending…" : "Send Reset Email"}
						</button>
					</section>
				)}
			</div>
		</div>
	)
}

export default SettingsPage
